import type { DigitalSummaryDto } from '@/types/dto';
import { renderBriefMarkdown } from '@/lib/brief-renderer';
import { Card, CardTitle, CardDescription } from '@/components/ui/card';
import { SourceList } from '@/components/brief/SourceList';

const themeOrder = ['competitor', 'customer', 'partner', 'regulation', 'technology'];

const themeLabels: Record<string, string> = {
  competitor: 'Competitors',
  customer: 'Customers (Operators & Contractors)',
  partner: 'Suppliers & Partners',
  regulation: 'Regulators & Standards',
  technology: 'Drilling-Digital Technology & Research',
};

export function DigitalSummary({ summary }: { summary: DigitalSummaryDto }) {
  const { digitalOverall, digitalOverallCited, themes, citationMap } = summary;

  const sourcesFor = (ids: string[]) => {
    const seen = new Set<string>();
    const out = [];
    for (const id of ids) {
      if (seen.has(id)) continue;
      seen.add(id);
      const c = citationMap[id];
      if (c) out.push(c);
    }
    return out;
  };

  const hasOverall = digitalOverall && digitalOverall.trim().length > 0;
  const hasThemes = Object.values(themes).some((t) => t.summary.trim().length > 0);

  if (!hasOverall && !hasThemes) {
    return (
      <Card>
        <CardDescription>
          No brief generated yet for {summary.window}. Run "daily-brief" from Debug.
        </CardDescription>
      </Card>
    );
  }

  return (
    <>
      {hasOverall && (
        <Card>
          <CardTitle>Digital — Aggregate</CardTitle>
          <p className="text-muted text-xs tracking-widest mt-2 mb-3 font-(family-name:--font-ui)">
            Executive synthesis across competitors, customers, partners, regulation, and technology
          </p>
          <div
            className="brief-prose brief-drop font-(family-name:--font-body) text-[0.95rem] leading-relaxed"
            dangerouslySetInnerHTML={{
              __html: renderBriefMarkdown(digitalOverall ?? '', citationMap),
            }}
          />
          <SourceList label="Source articles" sources={sourcesFor(digitalOverallCited)} />
        </Card>
      )}

      {themeOrder.map((t) => {
        const block = themes[t];
        if (!block || !block.summary.trim()) return null;
        return (
          <Card key={t}>
            <div className="flex items-baseline gap-2 mb-3 flex-wrap">
              <h2 className="text-2xl font-semibold">{themeLabels[t] ?? t}</h2>
              <span className="text-muted text-sm">· {block.count} items</span>
            </div>
            <div
              className="brief-prose font-(family-name:--font-body) text-[0.95rem] leading-relaxed"
              dangerouslySetInnerHTML={{
                __html: renderBriefMarkdown(block.summary, citationMap),
              }}
            />
            <SourceList label="Sources" sources={sourcesFor(block.citedIds)} />
          </Card>
        );
      })}
    </>
  );
}
